import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  deleteChannelMessage,
  recordSentMessage,
  updateChannelActive,
  updateChannelActiveOnPageHide,
  updateSeenTime,
  uploadChatFileToS3,
} from 'api';
import {
  CHAT_FILE_SENT_LABEL,
  SENT_TEXT_MAX_LENGTH,
  getChatFileKind,
} from './constants';
import { AxiosError } from 'axios';
import { FileEvent, MessageEvent, SignalEvent } from 'pubnub';
import { usePubNub } from 'pubnub-react';
import React, { ReactElement, useEffect, useState } from 'react';
import { useUserStore } from 'store';
import useToast from 'hooks/useToast';
import {
  TChannel,
  TChannelDetail,
  TMember,
  TMessageDetail,
  TSendMessage,
} from 'types';
import {
  DEBOUNCE_SEND_MESSAGE_ACTIVE,
  MESSAGE_SIZE,
  SIGNAL_EVENT,
  TEXT_STRING,
  queryKeys,
} from '../../constants';
import { normalizePubNubTimetoken } from '../../utils/pubnubTimetoken';
import {
  applyMessageRecallForMale,
  getTrustedRecallMessageNotification,
  isPubNubMessageDeletedAction,
  isRelevantRecallForChat,
  shouldHideMessageFromHistory,
} from '../../utils/chatRecallMessage';
import MessageInput from './MessageInput';
import MessageList from './MessageList';

type TMessage = {
  channel: string;
  message: TMessageDetail;
  timetoken: string | number;
  messageType?: string | number | undefined;
  uuid?: string | undefined;
  error?: string | undefined;
  actions?: Record<string, Record<string, unknown>>;
};

type Props = {
  channel: string;
  users?: TMember[];
  onCreateChannel?: () => Promise<TChannel | undefined>;
  channelData?: TChannelDetail;
  onBlur?: () => void;
  onFocus?: () => void;
  action?: ReactElement;
  refetch: () => void;
  refetchChannelDetail: () => void;
};

const Chat = ({
  channel,
  channelData,
  users = [],
  onCreateChannel,
  onBlur,
  onFocus,
  action,
  refetch: _refetch,
  refetchChannelDetail,
}: Props) => {
  // Hooks
  const pubnub = usePubNub();
  const { user, clientId } = useUserStore();
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useToast();
  const timeoutRef = React.useRef<NodeJS.Timeout>();
  // まだ /sent していない直近の送信本文（通知プレビュー用）。undefined なら保留なし
  const pendingSentTextRef = React.useRef<string>();
  const timeoutSeenRef = React.useRef<NodeJS.Timeout>();

  // States
  const [showTyping, setShowTyping] = useState<boolean>(false);
  const [messages, setMessages] = useState<TMessage[]>([]);
  const [deleteTargetTimetoken, setDeleteTargetTimetoken] = useState<
    string | null
  >(null);
  const [isRecallingMessage, setIsRecallingMessage] = useState(false);
  const recalledTimetokensRef = React.useRef<Set<string>>(new Set());
  const subscribeChannels = React.useMemo(() => {
    const personalChannel = user?.uuid || pubnub?.getUUID?.();
    return [channel, personalChannel].filter((value): value is string => !!value);
  }, [channel, user?.uuid, pubnub]);

  const currentUserUuid = user?.uuid ?? pubnub?.getUUID?.();

  const applyRecall = React.useCallback((timetoken: string | number) => {
    applyMessageRecallForMale(
      recalledTimetokensRef.current,
      setMessages,
      timetoken,
    );
  }, []);

  // Query
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [queryKeys.MESSAGE_LIST, channel],
    queryFn: async () => {
      return pubnub
        .fetchMessages({
          channels: [channel],
          count: MESSAGE_SIZE,
          includeMessageType: true,
          includeUUID: true,
          includeMeta: true,
          includeMessageActions: true,
          start: messages[messages.length - 1]?.timetoken,
        })
        .then((res) => res.channels[channel] || []);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam, _allPageParams) => {
      return lastPage.length >= MESSAGE_SIZE ? lastPageParam + 1 : undefined;
    },
    gcTime: 0,
    enabled: !!pubnub,
  });

  // Mutation
  const { mutate } = useMutation({
    mutationFn: (text: string) => {
      return updateChannelActive(channel, text);
    },
    retry: 0,
  });

  // 保留中の /sent はタイマー・unmount・ページ離脱のどれか1つだけが取り出して送る
  const takePendingSentText = React.useCallback((): string | undefined => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
    const text = pendingSentTextRef.current;
    pendingSentTextRef.current = undefined;
    return text;
  }, []);

  const { mutateAsync: mutateSeen } = useMutation({
    mutationFn: () => {
      return updateSeenTime(channel);
    },
    retry: 0,
  });

  const mapMessage = React.useCallback(
    (message: TMessageDetail) => {
      let url;
      // Handle PubNub file (from sendFile)
      if (message.message?.file) {
        url = pubnub.getFileUrl({
          channel: channel,
          id: message.message.file.id,
          name: message.message.file.name,
        });
      }
      // Note: For media array from backend, we don't set url here
      // MessageItem will extract media from message.message?.message || message.message
      // This keeps the message structure intact for MessageItem to process
      return {
        ...message,
        timetoken: normalizePubNubTimetoken(message.timetoken),
        url: url,
      };
    },
    [pubnub, channel],
  );

  const handleSignal = React.useCallback(
    (params: SignalEvent) => {
      const recallNotification = getTrustedRecallMessageNotification(
        params.message,
        params.publisher,
      );
      if (
        recallNotification &&
        isRelevantRecallForChat({
          notification: recallNotification,
          eventChannel: params.channel,
          chatChannel: channel,
          currentUserUuid,
        })
      ) {
        applyRecall(recallNotification.timetoken!);
        return;
      }

      if (params.channel !== channel) return;

      const isMySignal = [clientId, user?.uuid].includes(params.publisher);
      if (isMySignal) return;

      const message = params.message;
      switch (message.event || message) {
        case SIGNAL_EVENT.TYPING_ON:
          setShowTyping(true);
          break;
        case SIGNAL_EVENT.TYPING_OFF:
          setShowTyping(false);
          break;
        case SIGNAL_EVENT.DISBAND_MEMBER:
          refetchChannelDetail();
          break;
        default:
          break;
      }
    },
    [user, clientId, channel, refetchChannelDetail, currentUserUuid, applyRecall],
  );

  const handleSeenTime = React.useCallback(() => {
    // send seen time
    if (timeoutSeenRef.current) return;
    timeoutSeenRef.current = setTimeout(() => {
      mutateSeen();
      clearTimeout(timeoutSeenRef.current);
      timeoutSeenRef.current = undefined;
    }, DEBOUNCE_SEND_MESSAGE_ACTIVE);
  }, [mutateSeen]);

  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      const recallNotification = getTrustedRecallMessageNotification(
        event.message,
        event.publisher,
      );

      if (
        recallNotification &&
        isRelevantRecallForChat({
          notification: recallNotification,
          eventChannel: event.channel,
          chatChannel: channel,
          currentUserUuid,
        })
      ) {
        applyRecall(recallNotification.timetoken!);
        return;
      }

      if (channel === event.channel) {
        handleSeenTime();
        setMessages((messages) => {
          const eventTimetoken = normalizePubNubTimetoken(event.timetoken);
          const isDuplicate = messages.some(
            (message) =>
              normalizePubNubTimetoken(message.timetoken) === eventTimetoken,
          );
          if (isDuplicate) return messages;

          const newMessages = [
            mapMessage({
              channel: event.channel,
              message: event.message,
              timetoken: eventTimetoken,
              uuid: event.publisher,
            }),
            ...messages,
          ];

          // Auto-scroll when new message is added
          setTimeout(() => {
            const scrollableElement =
              document.getElementById('scrollableMessage');
            if (scrollableElement) {
              scrollableElement.scrollTop = 0;
            }
          }, 50);

          return newMessages;
        });
      }
    },
    [channel, handleSeenTime, mapMessage, currentUserUuid, applyRecall],
  );

  const handleMessageAction = React.useCallback(
    (event: {
      channel: string;
      event?: string;
      data?: { type?: string };
      messageTimetoken?: string | number;
    }) => {
      if (event.channel !== channel) return;
      if (!isPubNubMessageDeletedAction(event)) return;

      applyRecall(event.messageTimetoken);
    },
    [channel, applyRecall],
  );

  const handleFile = React.useCallback(
    (event: FileEvent) => {
      handleSeenTime();
      const eventTimetoken = normalizePubNubTimetoken(event.timetoken);
      setMessages((messages) => [
        mapMessage({
          channel: event.channel,
          message: { text: event.message.text, file: event.file },
          timetoken: eventTimetoken,
          uuid: event.publisher,
        }),
        ...messages,
      ]);
    },
    [handleSeenTime, mapMessage],
  );

  const sendMessage = React.useCallback(
    async (payload: TSendMessage) => {
      const { message, file } = payload;
      if (!file && !message.trim()) return;
      const fileKind = file ? getChatFileKind(file.type) : undefined;
      // PDF/動画は PubNub sendFile のサイズ上限を超えるため S3 経由で送る
      const isS3Kind = fileKind === 'pdf' || fileKind === 'video';
      let messageBody = `${message.trim()}\n`;
      if (file) messageBody += TEXT_STRING.MESSAGE.IMAGE;
      // PDF/動画は本文（無ければ種別ラベル）を通知・一覧プレビューに使う。
      // 画像・テキストの文言は従来のまま変えない
      const previewText =
        isS3Kind && fileKind
          ? message.trim() || CHAT_FILE_SENT_LABEL[fileKind]
          : messageBody;
      const messagePayload = {
        text: message,
      };
      let targetChannel = channel;
      if (onCreateChannel) {
        const newChannel = await onCreateChannel();
        if (newChannel?.id) {
          targetChannel = newChannel?.id;
        }
      }
      // Check PubNub UUID before publishing
      const currentUuid = pubnub?.getUUID();
      if (!currentUuid) {
        console.error('❌ [SEND_MESSAGE] PubNub UUID is not set!');
        throw new Error(TEXT_STRING.ERROR.PUBNUB_UUID_NOT_SET);
      }

      // アップロード失敗と PubNub 障害を切り分けられるよう、S3 は PubNub の try 外で扱う。
      // チャンネル確定後にアップロードする（作成失敗時の S3 孤児ファイルを防ぐ）
      let uploadedUrl: string | undefined;
      if (file && isS3Kind) {
        const uploaded = await uploadChatFileToS3(file);
        uploadedUrl = uploaded.uploadedUrl;
      }

      let publishResult: { timetoken?: string | number } | undefined;
      try {
        if (file && isS3Kind && uploadedUrl) {
          publishResult = await pubnub.publish({
            channel: targetChannel,
            message: {
              ...messagePayload,
              media: [
                {
                  type: file.type,
                  url: uploadedUrl,
                  fileName: file.name,
                  originalHeight: 0,
                  originalWidth: 0,
                },
              ],
            },
          });
        } else if (file) {
          publishResult = await pubnub.sendFile({
            channel: targetChannel,
            message: messagePayload,
            file: file,
          });
        } else if (message) {
          publishResult = await pubnub.publish({
            channel: targetChannel,
            message: messagePayload,
          });
        }
      } catch (pubnubError: any) {
        console.error('❌ [SEND_MESSAGE] PubNub publish error:', {
          error: pubnubError,
          message: pubnubError?.message,
          status: pubnubError?.status,
          category: pubnubError?.category,
          channel: targetChannel,
          uuid: currentUuid,
        });
        throw pubnubError;
      }

      // PDF/動画のみは空だと backend が「画像」文言にするため種別ラベルを渡す
      const sentText =
        isS3Kind && fileKind
          ? message.trim() || CHAT_FILE_SENT_LABEL[fileKind]
          : message.trim();
      // 台帳の記録を待つ間にページを離れても /sent を送れるよう、publish 直後から保留にする
      pendingSentTextRef.current = sentText.slice(0, SENT_TEXT_MAX_LENGTH);

      // 運営の未読バッジは利用者の送信だけを台帳から数えるため、送信ごとに必ず記録する
      // （/sent はデバウンスされるので連投分を取りこぼす）。
      // 次の参加者通知を受けた管理画面が未読を数え直すので、記録より先に通知すると
      // 台帳へ入る前の件数を読んでしまう。順序を保つためここで待つ。
      // 記録に失敗しても送信自体は成功しているのでチャットは止めない
      const sentTimetoken = normalizePubNubTimetoken(publishResult?.timetoken);
      const recorded = sentTimetoken
        ? await recordSentMessage(targetChannel, sentTimetoken)
        : false;

      const channelUsers = users.filter((item) => item.uuid !== user?.uuid);
      if (channelUsers && channelUsers.length > 0) {
        Promise.all(
          channelUsers.map(({ uuid }) => {
            if (!uuid) {
              return Promise.resolve();
            }
            return pubnub
              .publish({
                channel: uuid,
                message: {
                  channel: channel,
                  text: previewText,
                  // 台帳に無い送信は運営側でも未読に数えない。伝えずに通知すると
                  // 一覧のバッジだけがローカルに増えて合計と食い違ったままになる
                  ...(recorded ? {} : { unrecorded: true }),
                },
              })
              .catch((error) => {
                console.error(
                  '❌ [SEND_MESSAGE] Error publishing to user channel:',
                  error,
                );
              });
          }),
        );
      }

      // 記録を待つ間にページ離脱や unmount で送り済みなら保留は空になっている
      if (pendingSentTextRef.current === undefined || timeoutRef.current) return;
      timeoutRef.current = setTimeout(() => {
        const text = takePendingSentText();
        if (text !== undefined) mutate(text);
      }, DEBOUNCE_SEND_MESSAGE_ACTIVE);
    },
    [users, user, channel, onCreateChannel, pubnub, mutate, takePendingSentText],
  );

  const onTyping = React.useCallback(
    (status: boolean) => {
      // Send typing signal to the main channel
      pubnub.signal({
        message: status ? SIGNAL_EVENT.TYPING_ON : SIGNAL_EVENT.TYPING_OFF,
        channel: channel,
      });
    },
    [pubnub, channel],
  );

  const handleDeleteRequest = React.useCallback((timetoken: string) => {
    setDeleteTargetTimetoken(timetoken);
  }, []);

  const handleConfirmRecall = React.useCallback(async () => {
    if (!deleteTargetTimetoken) return;

    setIsRecallingMessage(true);
    const timetoken = normalizePubNubTimetoken(deleteTargetTimetoken);
    try {
      await deleteChannelMessage(channel, timetoken);
      applyMessageRecallForMale(
        recalledTimetokensRef.current,
        setMessages,
        timetoken,
      );
      await queryClient.removeQueries({
        queryKey: [queryKeys.MESSAGE_LIST, channel],
      });
      toastSuccess(TEXT_STRING.MESSAGE.RECALL_SUCCESS);
      setDeleteTargetTimetoken(null);
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.message || TEXT_STRING.MESSAGE.RECALL_FAILED
          : TEXT_STRING.MESSAGE.RECALL_FAILED;
      toastError(message);
    } finally {
      setIsRecallingMessage(false);
    }
  }, [channel, deleteTargetTimetoken, queryClient, toastError, toastSuccess]);

  // Effects
  useEffect(() => {
    if (data?.pages) {
      const reverseData = [...data.pages[data.pages.length - 1]]
        .reverse()
        .filter(
          (msg) =>
            !shouldHideMessageFromHistory({
              message: msg.message,
              actions: msg.actions,
              timetoken: msg.timetoken,
              recalledTimetokens: recalledTimetokensRef.current,
            }),
        );
      setMessages((prev) => {
        const existing = new Set(
          prev.map((msg) => normalizePubNubTimetoken(msg.timetoken)),
        );
        const incoming = reverseData
          .map(mapMessage)
          .filter(
            (msg) => !existing.has(normalizePubNubTimetoken(msg.timetoken)),
          );
        return incoming.length > 0 ? [...prev, ...incoming] : prev;
      });
    }
    return () => {};
  }, [data, mapMessage]);

  useEffect(() => {
    recalledTimetokensRef.current.clear();
    setMessages([]);
  }, [channel]);

  useEffect(() => {
    if (!pubnub || subscribeChannels.length === 0) {
      return;
    }

    const listenerParams = {
      message: handleMessage,
      signal: handleSignal,
      file: handleFile,
      messageAction: handleMessageAction,
    };

    pubnub.addListener(listenerParams);
    pubnub.subscribe({ channels: subscribeChannels });

    return () => {
      pubnub.unsubscribe({ channels: subscribeChannels });
      pubnub.removeListener(listenerParams);
    };
  }, [
    pubnub,
    subscribeChannels,
    handleMessage,
    handleSignal,
    handleFile,
    handleMessageAction,
  ]);

  useEffect(() => {
    return () => {
      const text = takePendingSentText();
      if (text !== undefined) mutate(text);
    };
  }, [mutate, takePendingSentText]);

  useEffect(() => {
    const flushPendingSent = () => {
      const text = takePendingSentText();
      if (text !== undefined) updateChannelActiveOnPageHide(channel, text);
    };
    const flushPendingSentIfHidden = () => {
      if (document.visibilityState === 'hidden') flushPendingSent();
    };
    window.addEventListener('pagehide', flushPendingSent);
    document.addEventListener('visibilitychange', flushPendingSentIfHidden);
    return () => {
      window.removeEventListener('pagehide', flushPendingSent);
      document.removeEventListener(
        'visibilitychange',
        flushPendingSentIfHidden,
      );
    };
  }, [channel, takePendingSentText]);

  // Auto-scroll to show new messages (especially when sending)
  useEffect(() => {
    if (messages.length > 0) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const scrollableElement = document.getElementById('scrollableMessage');
        if (scrollableElement) {
          // Scroll to top (since we're using flex-col-reverse, top is where new messages appear)
          scrollableElement.scrollTop = 0;
        }
      }, 100);
    }
  }, [messages.length]);

  return (
    <div className="h-full w-full fixed top-0 flex flex-col justify-between overflow-hidden">
      <MessageList
        isLoading={isLoading}
        messages={messages}
        fetchMore={fetchNextPage}
        hasMore={hasNextPage}
        channelData={channelData}
        onDeleteRequest={handleDeleteRequest}
      />
      <MessageInput
        sendMessage={sendMessage}
        onTyping={onTyping}
        showTyping={showTyping}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {action}

      {deleteTargetTimetoken !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              {TEXT_STRING.MESSAGE.RECALL_CONFIRM_TITLE}
            </p>
            <p className="text-sm text-gray-600 mb-6">
              {TEXT_STRING.MESSAGE.RECALL_CONFIRM_MESSAGE}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={isRecallingMessage}
                onClick={() => setDeleteTargetTimetoken(null)}
                className="flex-1 py-2.5 rounded-lg border border-neutral-300 text-neutral-700 disabled:opacity-50"
              >
                {TEXT_STRING.MESSAGE.CANCEL}
              </button>
              <button
                type="button"
                disabled={isRecallingMessage}
                onClick={handleConfirmRecall}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {TEXT_STRING.MESSAGE.RECALL}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
